import React from 'react';
import { cn } from '@/lib/utils';

type BadgeColor = 'blue' | 'green' | 'yellow' | 'red' | 'slate' | 'purple' | 'orange';

const colors: Record<BadgeColor, string> = {
  blue:   'bg-brand-700  text-white',
  green:  'bg-green-700  text-white',
  yellow: 'bg-amber-700  text-white',
  red:    'bg-red-700    text-white',
  slate:  'bg-slate-600  text-white',
  purple: 'bg-violet-700 text-white',
  orange: 'bg-orange-700 text-white',
};

interface BadgeProps {
  color?: BadgeColor;
  children: React.ReactNode;
  className?: string;
}

export function Badge({ color = 'slate', children, className }: BadgeProps) {
  return (
    <span className={cn(
      'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
      colors[color],
      className,
    )}>
      {children}
    </span>
  );
}

// Helper maps for domain values
export function statusColor(status: string): BadgeColor {
  return ({
    open:               'blue',
    in_progress:        'yellow',
    awaiting_approval:  'purple',
    approved:           'green',
    rejected:           'red',
    closed:             'slate',
  } as Record<string, BadgeColor>)[status] ?? 'slate';
}

export function priorityColor(priority: string): BadgeColor {
  return ({ low: 'slate', medium: 'blue', high: 'orange', critical: 'red' } as Record<string, BadgeColor>)[priority] ?? 'slate';
}

export function severityColor(severity: string): BadgeColor {
  return ({ info: 'blue', success: 'green', warning: 'yellow', critical: 'red' } as Record<string, BadgeColor>)[severity] ?? 'slate';
}

export function taskTypeColor(type: string): BadgeColor {
  return ({ preventive: 'green', predictive: 'blue', corrective: 'red' } as Record<string, BadgeColor>)[type] ?? 'slate';
}

export function criticalityColor(c: string): BadgeColor {
  return ({ low: 'slate', medium: 'blue', high: 'orange', critical: 'red' } as Record<string, BadgeColor>)[c] ?? 'slate';
}
